import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Plus, Pencil, Trash2, Eye, EyeOff, ArrowLeft, UserPlus, Mail } from "lucide-react";
import { Logo } from "@/components/Shared";
import { usePageTitle } from "@/hooks/usePageTitle";
import { Switch } from "@/components/ui/switch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("orbit_admin_token")}` });

const MODULE_TEXT_BLOCKS = [
  { id: "intranet", idx: 0, label: "Intranet" },
  { id: "operations", idx: 1, label: "Centre des opérations" },
  { id: "kpi", idx: 2, label: "Module KPI" },
  { id: "support", idx: 3, label: "Module support" },
  { id: "metier", idx: 4, label: "Modules métier" },
  { id: "magasin", idx: 5, label: "Interface magasin" },
];

const EDITABLE_BLOCKS = [
  { key: "hero.eyebrow", label: "Accueil — Hero surtitre (vert)" },
  { key: "hero.titleLines.0", label: "Accueil — Hero titre, ligne 1" },
  { key: "hero.titleLines.1", label: "Accueil — Hero titre, ligne 2" },
  { key: "hero.titleLines.2", label: "Accueil — Hero titre, ligne 3 (en bleu)" },
  { key: "hero.subtitle", label: "Accueil — Hero paragraphe" },
  { key: "pillars.title", label: "Accueil — section Solution, titre" },
  { key: "pillars.subtitle", label: "Accueil — section Solution, sous-titre" },
  { key: "sectors.title", label: "Accueil — section Pour qui ?, titre" },
  { key: "sectors.subtitle", label: "Accueil — section Pour qui ?, sous-titre" },
  { key: "testimonial.quote", label: "Accueil — témoignage, citation" },
  { key: "testimonial.ctaTitle", label: "Accueil — bloc CTA, titre" },
  { key: "footer.baseline", label: "Footer — phrase de marque" },
  ...MODULE_TEXT_BLOCKS.flatMap((m) => [
    { key: `featuresPage.modules.${m.idx}.titleMain`, label: `Module ${m.label} — titre` },
    { key: `featuresPage.modules.${m.idx}.titleSub`, label: `Module ${m.label} — sous-titre coloré` },
    { key: `featuresPage.modules.${m.idx}.subtitle`, label: `Module ${m.label} — description` },
    { key: `modulePages.${m.id}.intro`, label: `Module ${m.label} — intro (page dédiée)` },
  ]),
];

const EMPTY_POST = {
  slug: "",
  title_fr: "",
  title_en: "",
  excerpt_fr: "",
  excerpt_en: "",
  content_fr: "",
  content_en: "",
  cover_image: "",
  published: true,
};

const TABS = [
  { id: "content", label: "Contenu du site" },
  { id: "articles", label: "Articles" },
  { id: "settings", label: "Paramètres" },
  { id: "admins", label: "Administrateurs" },
  { id: "leads", label: "Demandes de démo" },
];

const inputCls =
  "h-11 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none";
const areaCls =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none";
const labelCls = "text-xs font-bold uppercase tracking-wider text-slate-500";

const AdminPage = () => {
  const navigate = useNavigate();
  usePageTitle("Back office");
  const [tab, setTab] = useState("content");
  const [ready, setReady] = useState(false);
  const [me, setMe] = useState("");

  const [blocks, setBlocks] = useState({});
  const [savingContent, setSavingContent] = useState(false);

  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [savingPost, setSavingPost] = useState(false);

  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  const [leads, setLeads] = useState([]);

  const [creds, setCreds] = useState({
    current_password: "",
    new_email: "",
    new_password: "",
    confirm_password: "",
  });
  const [savingCreds, setSavingCreds] = useState(false);

  const [admins, setAdmins] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [inviteName, setInviteName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteLink, setInviteLink] = useState("");
  const [sendingInvite, setSendingInvite] = useState(false);

  const handleAuthError = useCallback(
    (err) => {
      if (err.response?.status === 401) {
        localStorage.removeItem("orbit_admin_token");
        navigate("/admin/login");
      }
    },
    [navigate]
  );

  const loadAll = useCallback(async () => {
    try {
      const meRes = await axios.get(`${API}/auth/me`, { headers: authHeaders() });
      setMe(meRes.data.email);
      const [contentRes, postsRes, settingsRes, leadsRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/content`, { headers: authHeaders() }),
        axios.get(`${API}/admin/posts`, { headers: authHeaders() }),
        axios.get(`${API}/admin/settings`, { headers: authHeaders() }),
        axios.get(`${API}/admin/leads`, { headers: authHeaders() }),
        axios.get(`${API}/admin/users`, { headers: authHeaders() }),
      ]);
      const map = {};
      (contentRes.data || []).forEach((b) => {
        map[b.key] = { fr: b.fr || "", en: b.en || "" };
      });
      setBlocks(map);
      setPosts(postsRes.data || []);
      setSettings(settingsRes.data);
      setLeads(leadsRes.data || []);
      setAdmins(usersRes.data.users || []);
      setInvitations(usersRes.data.invitations || []);
      setReady(true);
    } catch (err) {
      handleAuthError(err);
    }
  }, [handleAuthError]);

  useEffect(() => {
    if (!localStorage.getItem("orbit_admin_token")) {
      navigate("/admin/login");
      return;
    }
    loadAll();
  }, [loadAll, navigate]);

  const logout = () => {
    localStorage.removeItem("orbit_admin_token");
    navigate("/admin/login");
  };

  const saveContent = async () => {
    setSavingContent(true);
    try {
      const payload = EDITABLE_BLOCKS.map((b) => ({
        key: b.key,
        fr: blocks[b.key]?.fr || "",
        en: blocks[b.key]?.en || "",
      }));
      await axios.put(`${API}/admin/content`, payload, { headers: authHeaders() });
      toast.success("Contenu enregistré. Visible sur le site immédiatement.");
    } catch (err) {
      handleAuthError(err);
      toast.error("Échec de l'enregistrement.");
    } finally {
      setSavingContent(false);
    }
  };

  const savePost = async () => {
    setSavingPost(true);
    try {
      if (editing.id) {
        await axios.put(`${API}/admin/posts/${editing.id}`, editing, { headers: authHeaders() });
      } else {
        await axios.post(`${API}/admin/posts`, editing, { headers: authHeaders() });
      }
      toast.success(editing.id ? "Article mis à jour." : "Article créé.");
      setEditing(null);
      const res = await axios.get(`${API}/admin/posts`, { headers: authHeaders() });
      setPosts(res.data || []);
    } catch (err) {
      handleAuthError(err);
      toast.error("Échec de l'enregistrement de l'article.");
    } finally {
      setSavingPost(false);
    }
  };

  const deletePost = async (post) => {
    if (!window.confirm(`Supprimer « ${post.title_fr} » ?`)) return;
    try {
      await axios.delete(`${API}/admin/posts/${post.id}`, { headers: authHeaders() });
      setPosts((p) => p.filter((x) => x.id !== post.id));
      toast.success("Article supprimé.");
    } catch (err) {
      handleAuthError(err);
      toast.error("Échec de la suppression.");
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(
        `${API}/admin/settings`,
        {
          demo_notification_email: settings.demo_notification_email,
          sections: settings.sections,
          social: settings.social || {},
          video_url: settings.video_url || "",
        },
        { headers: authHeaders() }
      );
      toast.success("Paramètres enregistrés.");
    } catch (err) {
      handleAuthError(err);
      toast.error("Échec de l'enregistrement.");
    } finally {
      setSavingSettings(false);
    }
  };

  const saveCredentials = async () => {
    if (!creds.current_password) {
      toast.error("Saisissez votre mot de passe actuel pour confirmer.");
      return;
    }
    if (!creds.new_email && !creds.new_password) {
      toast.error("Renseignez un nouvel email et/ou un nouveau mot de passe.");
      return;
    }
    if (creds.new_password && creds.new_password !== creds.confirm_password) {
      toast.error("La confirmation du mot de passe ne correspond pas.");
      return;
    }
    setSavingCreds(true);
    try {
      const { data } = await axios.put(
        `${API}/auth/credentials`,
        {
          current_password: creds.current_password,
          new_email: creds.new_email || null,
          new_password: creds.new_password || null,
        },
        { headers: authHeaders() }
      );
      if (data.token) localStorage.setItem("orbit_admin_token", data.token);
      setMe(data.email);
      toast.success(`Identifiants mis à jour (${data.email}).`);
      setCreds({ current_password: "", new_email: "", new_password: "", confirm_password: "" });
    } catch (err) {
      handleAuthError(err);
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Échec de la mise à jour.");
    } finally {
      setSavingCreds(false);
    }
  };

  const sendInvite = async () => {
    if (!inviteEmail.trim()) return;
    setSendingInvite(true);
    setInviteLink("");
    try {
      const { data } = await axios.post(
        `${API}/admin/users/invite`,
        { email: inviteEmail.trim(), name: inviteName.trim() },
        { headers: authHeaders() }
      );
      toast.success(`Invitation envoyée à ${inviteEmail.trim()}.`);
      setInviteLink(data.link);
      setInviteName("");
      setInviteEmail("");
      const res = await axios.get(`${API}/admin/users`, { headers: authHeaders() });
      setInvitations(res.data.invitations || []);
    } catch (err) {
      handleAuthError(err);
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Échec de l'envoi de l'invitation.");
    } finally {
      setSendingInvite(false);
    }
  };

  const removeAdmin = async (email) => {
    if (!window.confirm(`Retirer l'accès de ${email} ?`)) return;
    try {
      await axios.delete(`${API}/admin/users/${encodeURIComponent(email)}`, { headers: authHeaders() });
      const res = await axios.get(`${API}/admin/users`, { headers: authHeaders() });
      setAdmins(res.data.users || []);
      setInvitations(res.data.invitations || []);
      toast.success("Accès retiré.");
    } catch (err) {
      handleAuthError(err);
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Échec de la suppression.");
    }
  };

  if (!ready) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-slate-50" data-testid="admin-loading">
        <p className="text-sm text-slate-500">Chargement du back office…</p>
      </main>
    );
  }

  return (
    <main data-testid="admin-page" className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-[#0A0F1D]">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5">
          <div className="flex items-center gap-4">
            <Logo dark className="h-7" />
            <span className="rounded-full bg-emerald-500/15 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-emerald-400">
              Back office
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden text-xs text-slate-400 sm:block">{me}</span>
            <Link
              to="/"
              data-testid="admin-view-site-link"
              className="flex items-center gap-1.5 text-xs font-semibold text-slate-400 transition-colors hover:text-white"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Voir le site
            </Link>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="flex items-center gap-1.5 rounded-lg border border-white/15 px-3 py-1.5 text-xs font-semibold text-slate-300 transition-colors hover:border-white/30 hover:text-white"
            >
              <LogOut className="h-3.5 w-3.5" /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-5 py-8">
        <div className="flex flex-wrap gap-2" data-testid="admin-tabs">
          {TABS.map((item) => (
            <button
              key={item.id}
              data-testid={`admin-tab-${item.id}`}
              onClick={() => {
                setTab(item.id);
                setEditing(null);
              }}
              className={`rounded-full px-4 py-2 text-sm font-bold transition-colors ${
                tab === item.id ? "bg-slate-900 text-white" : "border border-slate-200 bg-white text-slate-600 hover:text-slate-900"
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>

        {tab === "content" && (
          <div className="mt-8" data-testid="admin-content-panel">
            <p className="max-w-2xl text-sm text-slate-500">
              Modifiez les textes principaux du site. Laissez les deux champs vides pour revenir au texte d'origine.
              Rechargez le site après enregistrement pour voir le changement.
            </p>
            <div className="mt-6 space-y-5">
              {EDITABLE_BLOCKS.map((block) => (
                <div key={block.key} className="rounded-2xl border border-slate-200 bg-white p-5">
                  <p className="font-display text-sm font-bold text-slate-900">{block.label}</p>
                  <div className="mt-3 grid gap-3 lg:grid-cols-2">
                    <div className="space-y-1.5">
                      <label className={labelCls}>Français</label>
                      <textarea
                        rows={2}
                        data-testid={`content-fr-${block.key}`}
                        value={blocks[block.key]?.fr || ""}
                        onChange={(e) =>
                          setBlocks((b) => ({ ...b, [block.key]: { ...b[block.key], fr: e.target.value } }))
                        }
                        className={areaCls}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className={labelCls}>Anglais</label>
                      <textarea
                        rows={2}
                        data-testid={`content-en-${block.key}`}
                        value={blocks[block.key]?.en || ""}
                        onChange={(e) =>
                          setBlocks((b) => ({ ...b, [block.key]: { ...b[block.key], en: e.target.value } }))
                        }
                        className={areaCls}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <button
              onClick={saveContent}
              disabled={savingContent}
              data-testid="content-save-btn"
              className="mt-6 rounded-xl bg-blue-600 px-7 py-3.5 text-sm font-bold text-white transition-colors hover:bg-blue-700 disabled:opacity-60"
            >
              {savingContent ? "Enregistrement…" : "Enregistrer les textes"}
            </button>
          </div>
        )}

        {tab === "articles" && !editing && (
          <div className="mt-8" data-testid="admin-articles-panel">
            <button
              onClick={() => setEditing({ ...EMPTY_POST })}
              data-testid="article-new-btn"
              className="flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-blue-700"
            >
              <Plus className="h-4 w-4" /> Nouvel article
            </button>
            <div className="mt-6 space-y-3">
              {posts.length === 0 && <p className="text-sm text-slate-500">Aucun article.</p>}
              {posts.map((post, i) => (
                <div
                  key={post.id}
                  data-testid={`admin-post-row-${i}`}
                  className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-4"
                >
                  {post.cover_image && (
                    <img src={post.cover_image} alt="" className="h-14 w-20 rounded-lg object-cover" />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-display text-sm font-bold text-slate-900">{post.title_fr}</p>
                    <p className="mt-0.5 text-xs text-slate-400">
                      /ressources/{post.slug} · {new Date(post.created_at).toLocaleDateString("fr-FR")}
                    </p>
                  </div>
                  <span
                    className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold ${
                      post.published ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"
                    }`}
                  >
                    {post.published ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                    {post.published ? "Publié" : "Brouillon"}
                  </span>
                  <button
                    onClick={() => setEditing({ ...post })}
                    data-testid={`article-edit-btn-${i}`}
                    className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition-colors hover:border-blue-300 hover:text-blue-600"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => deletePost(post)}
                    data-testid={`article-delete-btn-${i}`}
                    className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition-colors hover:border-rose-300 hover:text-rose-600"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "articles" && editing && (
          <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-7" data-testid="article-editor">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-lg font-bold text-slate-900">
                {editing.id ? "Modifier l'article" : "Nouvel article"}
              </h2>
              <button
                onClick={() => setEditing(null)}
                data-testid="article-cancel-btn"
                className="text-sm font-semibold text-slate-500 hover:text-slate-900"
              >
                Annuler
              </button>
            </div>
            <div className="mt-6 grid gap-5 lg:grid-cols-2">
              <div className="space-y-1.5">
                <label className={labelCls}>Titre (FR) *</label>
                <input
                  data-testid="article-title-fr-input"
                  value={editing.title_fr}
                  onChange={(e) => setEditing((p) => ({ ...p, title_fr: e.target.value }))}
                  className={inputCls}
                />
              </div>
              <div className="space-y-1.5">
                <label className={labelCls}>Titre (EN)</label>
                <input
                  data-testid="article-title-en-input"
                  value={editing.title_en}
                  onChange={(e) => setEditing((p) => ({ ...p, title_en: e.target.value }))}
                  className={inputCls}
                />
              </div>
              <div className="space-y-1.5">
                <label className={labelCls}>Slug (laisser vide = auto)</label>
                <input
                  data-testid="article-slug-input"
                  value={editing.slug}
                  onChange={(e) => setEditing((p) => ({ ...p, slug: e.target.value }))}
                  placeholder="mon-super-article"
                  className={inputCls}
                />
              </div>
              <div className="space-y-1.5">
                <label className={labelCls}>Image de couverture (URL)</label>
                <input
                  data-testid="article-cover-input"
                  value={editing.cover_image}
                  onChange={(e) => setEditing((p) => ({ ...p, cover_image: e.target.value }))}
                  placeholder="https://…"
                  className={inputCls}
                />
              </div>
              <div className="space-y-1.5">
                <label className={labelCls}>Résumé (FR)</label>
                <textarea
                  rows={2}
                  data-testid="article-excerpt-fr-input"
                  value={editing.excerpt_fr}
                  onChange={(e) => setEditing((p) => ({ ...p, excerpt_fr: e.target.value }))}
                  className={areaCls}
                />
              </div>
              <div className="space-y-1.5">
                <label className={labelCls}>Résumé (EN)</label>
                <textarea
                  rows={2}
                  data-testid="article-excerpt-en-input"
                  value={editing.excerpt_en}
                  onChange={(e) => setEditing((p) => ({ ...p, excerpt_en: e.target.value }))}
                  className={areaCls}
                />
              </div>
              <div className="space-y-1.5">
                <label className={labelCls}>Contenu (FR) — paragraphes séparés par une ligne vide</label>
                <textarea
                  rows={9}
                  data-testid="article-content-fr-input"
                  value={editing.content_fr}
                  onChange={(e) => setEditing((p) => ({ ...p, content_fr: e.target.value }))}
                  className={areaCls}
                />
              </div>
              <div className="space-y-1.5">
                <label className={labelCls}>Contenu (EN)</label>
                <textarea
                  rows={9}
                  data-testid="article-content-en-input"
                  value={editing.content_en}
                  onChange={(e) => setEditing((p) => ({ ...p, content_en: e.target.value }))}
                  className={areaCls}
                />
              </div>
            </div>
            <div className="mt-5 flex items-center gap-3">
              <Switch
                checked={editing.published}
                onCheckedChange={(v) => setEditing((p) => ({ ...p, published: v }))}
                data-testid="article-published-switch"
              />
              <span className="text-sm font-semibold text-slate-700">Publier l'article</span>
            </div>
            <button
              onClick={savePost}
              disabled={savingPost || !editing.title_fr.trim()}
              data-testid="article-save-btn"
              className="mt-6 rounded-xl bg-blue-600 px-7 py-3.5 text-sm font-bold text-white transition-colors hover:bg-blue-700 disabled:opacity-60"
            >
              {savingPost ? "Enregistrement…" : editing.id ? "Mettre à jour" : "Créer l'article"}
            </button>
          </div>
        )}

        {tab === "settings" && settings && (
          <div className="mt-8 max-w-2xl" data-testid="admin-settings-panel">
            <div className="rounded-2xl border border-slate-200 bg-white p-7">
              <h2 className="font-display text-lg font-bold text-slate-900">Réception des demandes de démo</h2>
              <p className="mt-1 text-sm text-slate-500">
                Chaque demande envoyée depuis le formulaire du site arrive sur cette adresse.
              </p>
              <input
                type="email"
                data-testid="settings-email-input"
                value={settings.demo_notification_email}
                onChange={(e) => setSettings((s) => ({ ...s, demo_notification_email: e.target.value }))}
                className={`${inputCls} mt-4`}
              />
            </div>
            <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-7">
              <h2 className="font-display text-lg font-bold text-slate-900">Sections de la page d'accueil</h2>
              <p className="mt-1 text-sm text-slate-500">Activez ou masquez des sections optionnelles.</p>
              <div className="mt-5 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-bold text-slate-800">Section « Résultats » (chiffres clés)</p>
                    <p className="text-xs text-slate-500">+30% de productivité, 95% de satisfaction…</p>
                  </div>
                  <Switch
                    checked={!!settings.sections?.stats}
                    onCheckedChange={(v) => setSettings((s) => ({ ...s, sections: { ...s.sections, stats: v } }))}
                    data-testid="toggle-section-stats"
                  />
                </div>
                <div className="flex items-center justify-between border-t border-slate-100 pt-4">
                  <div>
                    <p className="text-sm font-bold text-slate-800">Section « Ils en parlent » (témoignage + CTA)</p>
                    <p className="text-xs text-slate-500">Citation de Thomas D. et appel à l'action.</p>
                  </div>
                  <Switch
                    checked={!!settings.sections?.testimonial}
                    onCheckedChange={(v) =>
                      setSettings((s) => ({ ...s, sections: { ...s.sections, testimonial: v } }))
                    }
                    data-testid="toggle-section-testimonial"
                  />
                </div>
              </div>
            </div>
            <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-7">
              <h2 className="font-display text-lg font-bold text-slate-900">Vidéo de démonstration</h2>
              <p className="mt-1 text-sm text-slate-500">
                URL lue dans les modales « Voir la vidéo » du site. Accepte un lien MP4 direct ou une URL
                YouTube / Vimeo.
              </p>
              <input
                type="url"
                data-testid="settings-video-url-input"
                value={settings.video_url || ""}
                onChange={(e) => setSettings((s) => ({ ...s, video_url: e.target.value }))}
                placeholder="https://…/video.mp4 ou https://www.youtube.com/watch?v=…"
                className={`${inputCls} mt-4`}
              />
            </div>
            <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-7">
              <h2 className="font-display text-lg font-bold text-slate-900">Réseaux sociaux</h2>
              <p className="mt-1 text-sm text-slate-500">
                Ces liens apparaissent dans le footer du site. Laissez vide pour masquer une icône.
              </p>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                {[
                  { key: "linkedin", label: "LinkedIn", placeholder: "https://www.linkedin.com/company/…" },
                  { key: "twitter", label: "X / Twitter", placeholder: "https://x.com/…" },
                  { key: "youtube", label: "YouTube", placeholder: "https://www.youtube.com/@…" },
                  { key: "instagram", label: "Instagram", placeholder: "https://www.instagram.com/…" },
                ].map((s) => (
                  <div key={s.key} className="space-y-1.5">
                    <label className={labelCls}>{s.label}</label>
                    <input
                      type="url"
                      data-testid={`settings-social-${s.key}`}
                      value={settings.social?.[s.key] || ""}
                      onChange={(e) =>
                        setSettings((st) => ({ ...st, social: { ...(st.social || {}), [s.key]: e.target.value } }))
                      }
                      placeholder={s.placeholder}
                      className={inputCls}
                    />
                  </div>
                ))}
              </div>
            </div>
            <button
              onClick={saveSettings}
              disabled={savingSettings}
              data-testid="settings-save-btn"
              className="mt-6 rounded-xl bg-blue-600 px-7 py-3.5 text-sm font-bold text-white transition-colors hover:bg-blue-700 disabled:opacity-60"
            >
              {savingSettings ? "Enregistrement…" : "Enregistrer les paramètres"}
            </button>

            <div className="mt-10 rounded-2xl border border-slate-200 bg-white p-7" data-testid="admin-account-card">
              <h2 className="font-display text-lg font-bold text-slate-900">Mes identifiants de connexion</h2>
              <p className="mt-1 text-sm text-slate-500">
                Changez l'email et/ou le mot de passe de connexion au back office. Confirmez avec votre mot de passe
                actuel.
              </p>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <label className={labelCls}>Mot de passe actuel *</label>
                  <input
                    type="password"
                    data-testid="creds-current-password"
                    value={creds.current_password}
                    onChange={(e) => setCreds((c) => ({ ...c, current_password: e.target.value }))}
                    className={inputCls}
                    autoComplete="current-password"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className={labelCls}>Nouvel email</label>
                  <input
                    type="email"
                    data-testid="creds-new-email"
                    value={creds.new_email}
                    onChange={(e) => setCreds((c) => ({ ...c, new_email: e.target.value }))}
                    placeholder="Laisser vide pour ne pas changer"
                    className={inputCls}
                    autoComplete="off"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className={labelCls}>Nouveau mot de passe</label>
                  <input
                    type="password"
                    data-testid="creds-new-password"
                    value={creds.new_password}
                    onChange={(e) => setCreds((c) => ({ ...c, new_password: e.target.value }))}
                    placeholder="8 caractères minimum"
                    className={inputCls}
                    autoComplete="new-password"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className={labelCls}>Confirmer le mot de passe</label>
                  <input
                    type="password"
                    data-testid="creds-confirm-password"
                    value={creds.confirm_password}
                    onChange={(e) => setCreds((c) => ({ ...c, confirm_password: e.target.value }))}
                    className={inputCls}
                    autoComplete="new-password"
                  />
                </div>
              </div>
              <button
                onClick={saveCredentials}
                disabled={savingCreds}
                data-testid="creds-save-btn"
                className="mt-6 rounded-xl bg-slate-900 px-7 py-3.5 text-sm font-bold text-white transition-colors hover:bg-slate-700 disabled:opacity-60"
              >
                {savingCreds ? "Mise à jour…" : "Mettre à jour mes identifiants"}
              </button>
            </div>
          </div>
        )}

        {tab === "admins" && (
          <div className="mt-8 grid items-start gap-6 lg:grid-cols-[1fr_1.4fr]" data-testid="admin-users-panel">
            <div className="rounded-2xl border border-slate-200 bg-white p-7">
              <h2 className="flex items-center gap-2 font-display text-lg font-bold text-slate-900">
                <UserPlus className="h-5 w-5 text-blue-600" /> Inviter un administrateur
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                La personne reçoit un email avec un lien d'activation valable 48h pour créer son mot de passe.
              </p>
              <div className="mt-5 space-y-4">
                <div className="space-y-1.5">
                  <label className={labelCls}>Nom (optionnel)</label>
                  <input
                    data-testid="invite-name-input"
                    value={inviteName}
                    onChange={(e) => setInviteName(e.target.value)}
                    placeholder="Prénom Nom"
                    className={inputCls}
                  />
                </div>
                <div className="space-y-1.5">
                  <label className={labelCls}>Email *</label>
                  <input
                    type="email"
                    data-testid="invite-email-input"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="prenom@entreprise.fr"
                    className={inputCls}
                  />
                </div>
                <button
                  onClick={sendInvite}
                  disabled={sendingInvite || !inviteEmail.trim()}
                  data-testid="invite-send-btn"
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-6 py-3.5 text-sm font-bold text-white transition-colors hover:bg-blue-700 disabled:opacity-60"
                >
                  <Mail className="h-4 w-4" />
                  {sendingInvite ? "Envoi en cours…" : "Envoyer l'invitation"}
                </button>
                {inviteLink && (
                  <div className="rounded-xl bg-emerald-50 p-4" data-testid="invite-link-box">
                    <p className="text-xs font-bold text-emerald-700">Invitation créée. Lien d'activation :</p>
                    <p className="mt-1 break-all font-mono-orbit text-[11px] text-emerald-800">{inviteLink}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-6">
              <div className="rounded-2xl border border-slate-200 bg-white p-7">
                <h2 className="font-display text-lg font-bold text-slate-900">
                  Administrateurs ({admins.length})
                </h2>
                <div className="mt-4 space-y-3">
                  {admins.map((user, i) => (
                    <div
                      key={user.email}
                      data-testid={`admin-user-row-${i}`}
                      className="flex items-center gap-4 rounded-xl border border-slate-100 p-4"
                    >
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white">
                        {(user.name || user.email).slice(0, 2).toUpperCase()}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-bold text-slate-900">{user.email}</p>
                        <p className="text-xs text-slate-400">
                          {user.name} · depuis le {new Date(user.created_at).toLocaleDateString("fr-FR")}
                        </p>
                      </div>
                      {user.email === me ? (
                        <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-[10px] font-bold text-emerald-600">
                          Vous
                        </span>
                      ) : (
                        <button
                          onClick={() => removeAdmin(user.email)}
                          data-testid={`admin-user-delete-${i}`}
                          className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition-colors hover:border-rose-300 hover:text-rose-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {invitations.length > 0 && (
                <div className="rounded-2xl border border-slate-200 bg-white p-7" data-testid="admin-invitations-panel">
                  <h2 className="font-display text-lg font-bold text-slate-900">
                    Invitations en attente ({invitations.length})
                  </h2>
                  <div className="mt-4 space-y-3">
                    {invitations.map((inv, i) => (
                      <div
                        key={inv.email}
                        data-testid={`admin-invitation-row-${i}`}
                        className="flex items-center gap-4 rounded-xl border border-amber-100 bg-amber-50/50 p-4"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-bold text-slate-900">{inv.email}</p>
                          <p className="text-xs text-slate-500">
                            Expire le {new Date(inv.expires_at).toLocaleDateString("fr-FR")}
                          </p>
                        </div>
                        <button
                          onClick={() => removeAdmin(inv.email)}
                          data-testid={`admin-invitation-delete-${i}`}
                          className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-colors hover:border-rose-300 hover:text-rose-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {tab === "leads" && (
          <div className="mt-8" data-testid="admin-leads-panel">
            <p className="text-sm text-slate-500">{leads.length} demande(s) de démo reçue(s).</p>
            <div className="mt-4 space-y-3">
              {leads.length === 0 && <p className="text-sm text-slate-500">Aucune demande pour le moment.</p>}
              {leads.map((lead, i) => (
                <div key={lead.id || i} data-testid={`lead-row-${i}`} className="rounded-2xl border border-slate-200 bg-white p-5">
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                    <p className="font-display text-sm font-bold text-slate-900">
                      {lead.first_name} {lead.last_name}
                    </p>
                    <span className="rounded-full bg-blue-50 px-2.5 py-0.5 text-[11px] font-bold text-blue-600">
                      {lead.company}
                    </span>
                    <span className="text-xs text-slate-400">
                      {new Date(lead.created_at).toLocaleDateString("fr-FR", {
                        day: "numeric",
                        month: "short",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-slate-600">
                    {lead.email} · {lead.phone} · {lead.stores} points de vente
                  </p>
                  {lead.message && <p className="mt-2 text-sm italic text-slate-500">« {lead.message} »</p>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </main>
  );
};

export default AdminPage;
